/**
 * Helpers de fechas (sin dependencias). Semana ISO: lunes = 0.
 */

export function startOfDay(d: Date): Date {
  const r = new Date(d)
  r.setHours(0, 0, 0, 0)
  return r
}

export function endOfDay(d: Date): Date {
  const r = new Date(d)
  r.setHours(23, 59, 59, 999)
  return r
}

export function addDays(d: Date, days: number): Date {
  const r = new Date(d)
  r.setDate(r.getDate() + days)
  return r
}

export function startOfWeek(d: Date): Date {
  const r = startOfDay(d)
  const day = r.getDay() // 0=Sun ... 6=Sat
  const diff = (day + 6) % 7 // distancia al lunes anterior
  return addDays(r, -diff)
}

export function endOfWeek(d: Date): Date {
  return endOfDay(addDays(startOfWeek(d), 6))
}

export function startOfMonth(d: Date): Date {
  return startOfDay(new Date(d.getFullYear(), d.getMonth(), 1))
}

export function endOfMonth(d: Date): Date {
  return endOfDay(new Date(d.getFullYear(), d.getMonth() + 1, 0))
}

/** Grid 6×7 que cubre todo el mes empezando en lunes. */
export function monthGridStart(d: Date): Date {
  return startOfWeek(startOfMonth(d))
}

export function monthGridEnd(d: Date): Date {
  return endOfWeek(endOfMonth(d))
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

export function isSameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()
}

const DAY_NAMES = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']
const MONTH_NAMES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
]

export function dayLabelShort(d: Date): string {
  return DAY_NAMES[(d.getDay() + 6) % 7]!
}

export function monthLabel(d: Date): string {
  return MONTH_NAMES[d.getMonth()]!
}

export function formatTime(d: Date): string {
  return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
}

export function rangeLabel(from: Date, to: Date): string {
  const sameMonth = from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear()
  if (sameMonth) {
    return `${from.getDate()} – ${to.getDate()} ${monthLabel(from)} ${from.getFullYear()}`
  }
  return `${from.getDate()} ${monthLabel(from)} – ${to.getDate()} ${monthLabel(to)} ${to.getFullYear()}`
}

/**
 * Convierte un Date a string para input[type=datetime-local].
 * Mantiene la zona horaria local del navegador.
 */
export function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function fromLocalInput(s: string): Date {
  return new Date(s)
}
